import { useEffect, useState, useCallback } from "react";
import { restoreSession, currentUser, hasSession, dropSession, type User } from "./api";
import { onSync, startAutoSync, stopAutoSync, sync, type SyncState } from "./sync";
import { setupUpdates, applyUpdate } from "./update";
import Login from "./screens/Login";
import Invite from "./screens/Invite";
import Points from "./screens/Points";
import PointCard from "./screens/PointCard";
import PointForm from "./screens/PointForm";
import VisitForm from "./screens/VisitForm";
import ContactForm from "./screens/ContactForm";
import Stock from "./screens/Stock";
import Profile from "./screens/Profile";
import Staff from "./screens/Staff";
import Issue from "./screens/Issue";
import Export from "./screens/Export";
import Help from "./screens/Help";

export type Route =
  | { name: "points" }
  | { name: "point"; id: string }
  | { name: "pointForm"; id?: string }
  | { name: "visitForm"; pointId: string; visitId?: string }
  | { name: "contactForm"; pointId: string }
  | { name: "stock" }
  | { name: "profile" }
  | { name: "staff" }
  | { name: "issue" }
  | { name: "export" }
  | { name: "help" };

export type Nav = (r: Route) => void;

export function SyncPill({ state }: { state: SyncState }) {
  const pending = state.pending + state.photosPending;
  if (state.running) {
    return <span className="sync off"><span className="spin" />синхронизация</span>;
  }
  // Ошибку показываем раньше всего остального: зелёная плашка при сломанной
  // синхронизации — худшее, что можно показать человеку в поле.
  if (state.error) {
    return <span className="sync err" title={state.error}><span className="dot" />ошибка обмена</span>;
  }
  if (pending > 0) {
    return <span className="sync wait"><span className="dot" />не отправлено: {pending}</span>;
  }
  if (!navigator.onLine) {
    return <span className="sync off"><span className="dot" />нет сети</span>;
  }
  if (!state.lastSync) {
    return <span className="sync off"><span className="dot" />ещё не обменивались</span>;
  }
  return <span className="sync ok"><span className="dot" />всё отправлено</span>;
}

export default function App() {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [route, setRoute] = useState<Route>({ name: "points" });
  const [stack, setStack] = useState<Route[]>([]);
  const [state, setState] = useState<SyncState>({
    running: false, lastSync: null, pending: 0, photosPending: 0, error: null,
  });
  const [updateReady, setUpdateReady] = useState(false);

  // Ссылка-приглашение приходит как /invite/<токен> — её надо поймать до входа.
  const inviteToken = location.pathname.startsWith("/invite/")
    ? decodeURIComponent(location.pathname.slice("/invite/".length))
    : null;

  useEffect(() => {
    void (async () => {
      await restoreSession();
      setUser(currentUser());
      setReady(true);
    })();
    setupUpdates(() => setUpdateReady(true));
    return onSync((s) => {
      setState(s);
      // Обмен мог принести новые данные о сотруднике — например, назначенный бренд.
      const fresh = currentUser();
      setUser((u) => (u && fresh && (u.brandId ?? null) !== (fresh.brandId ?? null) ? fresh : u));
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    startAutoSync();
    return stopAutoSync;
  }, [user]);

  const nav = useCallback<Nav>((r) => {
    setStack((s) => [...s, route]);
    setRoute(r);
  }, [route]);

  const back = useCallback(() => {
    setStack((s) => {
      if (!s.length) { setRoute({ name: "points" }); return s; }
      setRoute(s[s.length - 1]);
      return s.slice(0, -1);
    });
  }, []);

  // Аппаратная кнопка «назад» на планшете должна возвращать по экранам,
  // а не закрывать приложение.
  useEffect(() => {
    history.pushState(null, "");
    const onPop = () => { back(); history.pushState(null, ""); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [back]);

  const onLoggedIn = useCallback((u: User) => {
    setUser(u);
    setRoute({ name: "points" });
    setStack([]);
    if (location.pathname !== "/") history.replaceState(null, "", "/");
  }, []);

  const onLogout = useCallback(async () => {
    stopAutoSync();
    await dropSession();
    setUser(null);
    setRoute({ name: "points" });
    setStack([]);
  }, []);

  if (!ready) {
    return <div className="app"><div className="body"><div className="empty">Загрузка…</div></div></div>;
  }

  if (inviteToken && !hasSession()) {
    return <Invite token={inviteToken} onDone={onLoggedIn} />;
  }
  if (!user) return <Login onDone={onLoggedIn} />;

  const shared = { nav, back, user, state, onSync: () => void sync(), updateReady };

  switch (route.name) {
    case "point":       return <PointCard {...shared} pointId={route.id} />;
    case "pointForm":   return <PointForm {...shared} pointId={route.id} />;
    case "visitForm":   return <VisitForm {...shared} pointId={route.pointId} visitId={route.visitId} />;
    case "contactForm": return <ContactForm {...shared} pointId={route.pointId} />;
    case "stock":       return <Stock {...shared} />;
    case "profile":     return <Profile {...shared} onLogout={onLogout} />;
    case "staff":       return <Staff {...shared} />;
    case "issue":       return <Issue {...shared} />;
    case "export":      return <Export {...shared} />;
    case "help":        return <Help {...shared} />;
    default:            return <Points {...shared} />;
  }
}
